'use client';

import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { useSectionMotionVariants } from '@/components/shared/motion';
import {
  useScrollRevealMotion,
  type ScrollRevealMotionOptions,
} from '@/hooks/use-scroll-reveal-motion';
import { cn } from '@/lib/utils';

interface SectionHeaderContent {
  eyebrow?: string | null;
  title?: {
    text: string;
    emphasis?: string;
    suffix?: string;
  };
  description?: string | null;
}

export default function SectionHeader({
  content,
  className,
  eyebrowClassName,
  emphasisClassName,
  headingClassName,
  suffixClassName,
  subheadingClassName,
  titleSlot,
  titleSlotClassName,
  descriptionSlot,
  viewportMargin,
  /** When true, omit in-view triggering; animate as a variant child of a parent `sectionSequenceRevealVariants`. */
  sequenceChild = false,
  children,
}: {
  content: SectionHeaderContent;
  className?: string;
  eyebrowClassName?: string;
  emphasisClassName?: string;
  headingClassName?: string;
  subheadingClassName?: string;
  suffixClassName?: string;
  titleSlot?: ReactNode;
  /** Applied to the motion wrapper around `titleSlot` (e.g. base text color for portable text). */
  titleSlotClassName?: string;
  descriptionSlot?: ReactNode;
  viewportMargin?: ScrollRevealMotionOptions['margin'];
  sequenceChild?: boolean;
  children?: ReactNode;
}) {
  const { sectionHeaderItemVariants, sectionHeaderRevealVariants } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion(
    viewportMargin !== undefined ? { margin: viewportMargin } : undefined,
  );

  return (
    <motion.div
      {...(sequenceChild ? {} : scrollReveal)}
      variants={sectionHeaderRevealVariants}
      className={cn('space-y-3', className)}
    >
      {content.eyebrow ? (
        <motion.div
          variants={sectionHeaderItemVariants}
          className={cn(
            'text-[10px] sm:text-xs font-medium uppercase tracking-widest',
            eyebrowClassName,
          )}
        >
          {content.eyebrow}
        </motion.div>
      ) : null}
      {titleSlot ? (
        <motion.div variants={sectionHeaderItemVariants} className={cn(titleSlotClassName)}>
          {titleSlot}
        </motion.div>
      ) : content.title ? (
        <motion.h2
          variants={sectionHeaderItemVariants}
          className={cn(
            'text-3xl sm:text-5xl leading-tight font-light tracking-tight',
            headingClassName,
          )}
        >
          {content.title.text}&nbsp;
          {content.title.emphasis && (
            <span className={cn('italic font-normal', emphasisClassName)}>
              {content.title.emphasis}
            </span>
          )}
          {content.title.suffix && (
            <span className={cn('italic font-normal', suffixClassName)}>
              {' '}
              {content.title.suffix}
            </span>
          )}
        </motion.h2>
      ) : null}
      {descriptionSlot ? (
        <motion.div variants={sectionHeaderItemVariants}>{descriptionSlot}</motion.div>
      ) : content.description ? (
        <motion.p
          variants={sectionHeaderItemVariants}
          className={cn(
            'max-w-4xl text-sm sm:text-base leading-relaxed font-light',
            subheadingClassName,
          )}
        >
          {content.description}
        </motion.p>
      ) : null}
      {children}
    </motion.div>
  );
}
